<template>
  <div class="tw-bg-white tw-shadow-md tw-rounded-xl tw-p-6 tw-border tw-border-amber-200">
    <div class="tw-flex tw-items-start tw-space-x-3 tw-mb-4">
      <div class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-50">
        <svg
          class="tw-h-5 tw-w-5 tw-text-amber-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
          />
        </svg>
      </div>
      <div>
        <h3 class="tw-text-base tw-font-semibold tw-text-slate-900">
          Amendments required
        </h3>
        <p class="tw-mt-1 tw-text-sm tw-text-slate-600">
          You've selected add-ons for your quote. Fill in your details below and submit to request these changes.
        </p>
      </div>
    </div>

    <!-- Selected add-ons list -->
    <div class="tw-mb-4">
      <p class="tw-text-[11px] tw-font-medium tw-text-slate-500 tw-uppercase tw-tracking-wide tw-mb-2">
        Selected add-ons ({{ selectedAddonsList.length }})
      </p>
      <div class="tw-space-y-2">
        <div
          v-for="addon in selectedAddonsList"
          :key="addon.key"
          class="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-blue-200 tw-bg-blue-50 tw-px-3 tw-py-2"
        >
          <div class="tw-flex-1 tw-min-w-0">
            <p class="tw-text-xs tw-font-semibold tw-text-blue-900 tw-truncate">
              {{ addon.label }}
            </p>
            <p
              v-if="addon.itemName"
              class="tw-text-[11px] tw-text-blue-700 tw-truncate"
            >
              on {{ addon.itemName }}
            </p>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <span class="tw-text-xs tw-font-semibold tw-text-blue-900 tw-whitespace-nowrap">
              {{ formatCurrency(addon.price) }}
            </span>
            <button
              type="button"
              class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-100 tw-text-blue-600 tw-h-5 tw-w-5 hover:tw-bg-blue-200 tw-flex-shrink-0"
              aria-label="Remove add-on"
              @click="$emit('remove-addon', addon)"
            >
              <span class="tw-text-xs tw-leading-none">×</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Requester details -->
    <div class="tw-mb-4 tw-space-y-3">
      <div>
        <label class="tw-block tw-text-xs tw-font-medium tw-text-slate-600 tw-uppercase tw-tracking-wide">
          Your name
        </label>
        <input
          :value="requesterName"
          type="text"
          class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
          placeholder="e.g. John Smith"
          required
          @input="$emit('update:requesterName', $event.target.value)"
        >
      </div>
      <div>
        <label class="tw-block tw-text-xs tw-font-medium tw-text-slate-600 tw-uppercase tw-tracking-wide">
          Your email
        </label>
        <input
          :value="requesterEmail"
          type="email"
          class="tw-mt-1 tw-block tw-w-full tw-rounded-md tw-border tw-border-slate-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-border-blue-500 focus:tw-ring-blue-500"
          placeholder="you@example.com"
          required
          @input="$emit('update:requesterEmail', $event.target.value)"
        >
      </div>
    </div>

    <!-- Actions -->
    <div class="tw-space-y-2">
      <button
        type="button"
        class="tw-w-full tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-transparent tw-bg-blue-600 tw-px-4 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white tw-shadow-sm hover:tw-bg-blue-700 disabled:tw-opacity-60"
        :disabled="submitting"
        @click="$emit('submit')"
      >
        <span v-if="submitting">Submitting…</span>
        <span v-else>Submit amendments</span>
      </button>
      <button
        type="button"
        class="tw-w-full tw-inline-flex tw-justify-center tw-rounded-md tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50"
        :disabled="submitting"
        @click="$emit('clear')"
      >
        Clear selections
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AddonAmendmentsCard',
  props: {
    selectedAddonsList: {
      type: Array,
      default: () => []
    },
    requesterName: {
      type: String,
      default: ''
    },
    requesterEmail: {
      type: String,
      default: ''
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'submit',
    'clear',
    'remove-addon',
    'update:requesterName',
    'update:requesterEmail'
  ],
  setup() {
    const formatCurrency = (amount) => {
      const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(numericAmount || 0)
    }

    return {
      formatCurrency
    }
  }
}
</script>
